import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WifiOff, Wifi, RefreshCw, CheckCircle2, X } from 'lucide-react';
import useOnlineStatus from '@/hooks/useOnlineStatus';

/**
 * Global In-App Offline Notification Banner
 * Appears when network connection is lost, provides manual retry,
 * and celebrates with a green reconnection badge when back online.
 */
export const OfflineBanner = () => {
  const { t } = useTranslation('common');
  const { isOffline, wasOffline, isChecking, checkConnectivity } = useOnlineStatus();
  const [isDismissed, setIsDismissed] = useState(false);

  // If online and wasn't offline previously, or user manually dismissed this offline session
  const showOffline = isOffline && !isDismissed;
  const showReconnected = !isOffline && wasOffline;

  if (!showOffline && !showReconnected) {
    return null;
  }

  return (
    <aside
      aria-label="Network status banner"
      className="fixed top-0 left-0 right-0 z-[9999] pointer-events-none flex justify-center px-3 pt-safe transition-all duration-300 ease-out"
    >
      <div
        className={`pointer-events-auto mt-2 max-w-xl w-full mx-auto px-4 py-2.5 rounded-2xl shadow-xl backdrop-blur-xl border transition-all duration-300 transform ${
          showReconnected
            ? 'bg-emerald-950/90 border-emerald-500/40 text-emerald-100 shadow-emerald-950/40 translate-y-0 scale-100'
            : 'bg-stone-900/90 border-amber-500/40 text-stone-100 shadow-black/50 translate-y-0 scale-100'
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          {/* Status Icon & Message */}
          <div className="flex items-center gap-3 min-w-0">
            {showReconnected ? (
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-400">
                <CheckCircle2 className="w-4 h-4 animate-bounce" />
              </div>
            ) : (
              <div className="relative flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400">
                <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
                </span>
                <WifiOff className="w-4 h-4" />
              </div>
            )}

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold tracking-wide uppercase">
                  {showReconnected ? t('online', 'Online') : t('offlineBannerTitle', "You're offline")}
                </span>
              </div>
              <p className="text-xs opacity-90 truncate">
                {showReconnected
                  ? t('offlineReconnected', 'Back online! Connection restored.')
                  : t('offlineBannerDesc', 'Check your connection. Reconnecting automatically...')}
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {showOffline && (
              <button
                type="button"
                onClick={() => checkConnectivity()}
                disabled={isChecking}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/30 active:scale-95 transition-all disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isChecking ? 'animate-spin' : ''}`} />
                <span>{isChecking ? t('offlineChecking', 'Checking...') : t('retry', 'Retry')}</span>
              </button>
            )}

            {showOffline && (
              <button
                type="button"
                onClick={() => setIsDismissed(true)}
                className="p-1 rounded-md text-stone-400 hover:text-stone-200 hover:bg-white/10 transition-colors"
                aria-label="Dismiss banner"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default OfflineBanner;
